/**
 * Sentry bootstrap for Subiza.
 *
 * IMPORTANT — why this file looks the way it does
 * ------------------------------------------------
 * 1. Env access: this module is bundled for the BROWSER, so it must read
 *    `import.meta.env.VITE_*`. `process.env` does not exist in a browser
 *    bundle (Vite never polyfills it), so reading `process.env.VITE_SENTRY_DSN`
 *    here threw `ReferenceError: process is not defined` on every page load and
 *    Sentry never initialized. Server-only `process.env` reads belong in
 *    `*.server.js` files only — see src/lib/config.server.js for that pattern.
 *
 * 2. Tracing: `BrowserTracing` from `@sentry/tracing` (v7) is incompatible with
 *    `@sentry/react` v11, which ships its own `browserTracingIntegration`.
 *    Mixing them loaded two copies of `@sentry/core` (7.x and 11.x), which
 *    breaks span/transaction correlation. v8+ removed `@sentry/tracing` entirely.
 *
 * 3. Init timing: Sentry must be initialized before the router renders, or
 *    bootstrap errors (module evaluation, provider setup, hydration) are thrown
 *    before any handler exists and are lost forever. `src/router.jsx` calls
 *    `initSentry()` at the top of `getRouter()`, which runs before the first
 *    render. Do NOT move this back into a React effect.
 *
 * 4. Idempotency: `getRouter()` can run more than once (HMR, multiple mounts),
 *    and calling `Sentry.init()` twice leaks the first client. The `initialized`
 *    guard makes repeated calls a no-op.
 *
 * Required env (see .env.example):
 *   VITE_SENTRY_DSN                    https://sentry.io -> Settings -> Client Keys
 * Optional env:
 *   VITE_SENTRY_ENVIRONMENT            default: "production" / "development"
 *   VITE_SENTRY_RELEASE                release name, e.g. "subiza@a1b2c3d"
 *   VITE_SENTRY_TRACES_SAMPLE_RATE     number 0..1, default 0.2 in prod / 1.0 in dev
 *   VITE_SENTRY_TRACE_TARGETS          comma-separated hosts to propagate traces to
 *   VITE_SENTRY_REPLAYS                "true" to record session replay (off by default)
 *   VITE_SENTRY_SEND_EMAIL             "true" to attach user emails (PII, off by default)
 */

import * as Sentry from "@sentry/react";

/**
 * Vite statically replaces `import.meta.env.*` at build time, so these reads
 * are literal string lookups in the output bundle — no runtime cost, no
 * `process` reference, nothing to strip.
 *
 * The `?? {}` guard matters: this module is also evaluated by the Nitro
 * (Cloudflare Workers) server bundle, where `import.meta.env` may be absent or
 * partial. Without the guard the first property read throws a TypeError during
 * SSR, which would break the page instead of just disabling monitoring.
 */
const ENV = import.meta.env ?? {};

/** Sentry is a no-op unless a real DSN is configured. */
export const SENTRY_ENABLED = Boolean(ENV.VITE_SENTRY_DSN);

/** Production builds set this to `true`; use it to separate noisy dev defaults. */
const IS_PROD = Boolean(ENV.PROD);

/**
 * Release identifier. Without it, Sentry cannot tell you which deploy a stack
 * trace came from, which is the difference between "it broke" and "the Tuesday
 * deploy broke it". `scripts/sentry-release.mjs` computes the value from git and
 * passes it in as `VITE_SENTRY_RELEASE` during Cloudflare Pages builds.
 */
const RELEASE = ENV.VITE_SENTRY_RELEASE || undefined;

/**
 * Environment label. Set independently of production so staging deploys are
 * not mixed into the production dataset.
 */
const ENVIRONMENT = ENV.VITE_SENTRY_ENVIRONMENT || (IS_PROD ? "production" : "development");

/**
 * Transaction sampling. 1.0 sends every navigation to Sentry: accurate, but
 * expensive and noisy. 0.2 keeps a representative fifth of traffic, which is
 * enough to catch regressions while staying cheap. Local dev uses 1.0 so
 * tracing is visible while developing.
 */
const TRACES_SAMPLE_RATE = Number(ENV.VITE_SENTRY_TRACES_SAMPLE_RATE ?? (IS_PROD ? 0.2 : 1.0));

/**
 * Session replay records DOM snapshots and user input. It is the single most
 * useful tool for "the page is blank" reports and the biggest privacy risk, so
 * it stays opt-in and masks all text and media when enabled.
 */
const REPLAYS_ENABLED = ENV.VITE_SENTRY_REPLAYS === "true";

/** Attaching emails is PII. Off unless explicitly enabled. */
const SEND_EMAIL = ENV.VITE_SENTRY_SEND_EMAIL === "true";

/**
 * True only in a real browser. Sentry is initialized client-side; on the server
 * this module is imported too (src/start.js pulls in shared code), so every
 * browser-only API below is guarded.
 */
const IS_BROWSER = typeof window !== "undefined" && typeof window.location !== "undefined";

/**
 * Cross-origin trace propagation only happens for hosts we control. Sending
 * trace headers to unrelated third parties leaks request correlation data.
 *
 * `VITE_SENTRY_TRACE_TARGETS` accepts a comma-separated host list, which lets
 * staging use a different allowlist than production without a code change.
 *
 * @returns {(string | RegExp)[]}
 */
function parseTracePropagationTargets() {
  const configured = ENV.VITE_SENTRY_TRACE_TARGETS;

  if (configured) {
    const targets = configured
      .split(",")
      .map((host) => host.trim())
      .filter(Boolean)
      // A bare hostname matches any scheme on that host.
      .map((host) => new RegExp(`^https?://${escapeForRegExp(host)}(/.*)?$`));

    if (targets.length > 0) return targets;
  }

  // Dev default, matching Sentry's own: localhost plus same-origin requests.
  // `^\/` is a URL-relative pattern, so same-origin XHR/fetch calls are traced.
  if (!IS_PROD) return ["localhost", /^\//];

  // Production default: trust only the origin the app was served from.
  // Cloudflare Pages serves the app and its API from the same origin, so this
  // covers `/api/*` requests. Override via VITE_SENTRY_TRACE_TARGETS when the
  // API moves to a separate host.
  return IS_BROWSER ? [window.location.origin] : [];
}

/** Escapes a hostname so dots and dashes are matched literally, not as regex. */
function escapeForRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Extra noise to drop on top of Sentry's built-in ignore list.
 *
 * Note what is deliberately absent: "AbortError" and "Failed to fetch" are NOT
 * filtered. TanStack Router already swallows superseded prefetches internally,
 * so an AbortError that reaches here is a real bug (a cancelled mutation, a
 * query aborted by unmount), and Sentry's defaults already cover plain network
 * failures. Broadly ignoring them is how monitoring quietly stops working.
 *
 * Every entry should be a message confirmed harmless by a human.
 */
const IGNORE_ERRORS = [
  // Fired whenever a responsive container resizes; harmless and very common
  // with the app's sidebar/resizable-panel layouts.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];

/** Guards against double `Sentry.init()` (HMR, multiple router instances). */
let initialized = false;

/**
 * Initialize Sentry once per page load.
 *
 * Safe to call from multiple places and a no-op when no DSN is configured, so
 * callers do not need to branch on `SENTRY_ENABLED`.
 *
 * @param {object} [overrides] - Extra options merged over the defaults. Used by
 *   tests to inject a fake transport without touching the real project.
 */
export function initSentry(overrides = {}) {
  if (initialized) return;
  initialized = true;

  // Browser-only, and this is load-bearing rather than cosmetic. TanStack Start
  // also evaluates this module in the Nitro/Cloudflare Workers bundle during
  // SSR. Initializing the *browser* SDK in a worker installs DOM-dependent
  // integrations against a runtime that has no DOM, which either throws or
  // silently ships broken events.
  //
  // Server-side errors (Gemini calls, Supabase service-role queries) therefore
  // are NOT reported yet. That needs the Cloudflare SDK:
  //   npm i @sentry/cloudflare
  //   Sentry.init({ dsn: env.SENTRY_DSN }) inside Nitro's request handler,
  // then wrap the try/catch in src/start.js. Not done here because it adds a
  // second SDK and a second transport to maintain.
  if (!IS_BROWSER) return;

  // Without a DSN, `Sentry.init` still installs global error handlers and
  // buffers events. Bail out so local dev is not slowed by a no-op SDK.
  if (!SENTRY_ENABLED) {
    console.info("[sentry] VITE_SENTRY_DSN not set - error monitoring disabled");
    return;
  }

  Sentry.init({
    dsn: ENV.VITE_SENTRY_DSN,
    enabled: true,

    // --- Deploy visibility -------------------------------------------------
    // Release + environment make "which deploy broke?" answerable in the UI.
    release: RELEASE,
    environment: ENVIRONMENT,

    // --- Integrations ------------------------------------------------------
    integrations: [
      // Replaces the v7 `@sentry/tracing` BrowserTracing. Must come from
      // @sentry/react in v8+; the standalone package no longer exists.
      Sentry.browserTracingIntegration({
        tracePropagationTargets: parseTracePropagationTargets(),
      }),

      // Default integrations (global handlers for `error` + `unhandledrejection`,
      // console breadcrumbs, Breadcrumbs/History/Context API instrumentation)
      // are already applied by Sentry v11 — do not re-add them here.
    ],

    // --- Sampling ----------------------------------------------------------
    tracesSampleRate: TRACES_SAMPLE_RATE,

    // Errors are never sampled: losing one is far more costly than the quota.
    // `sampleRate` only applies if it is ever enabled explicitly.
    sampleRate: 1.0,

    // Turn unhandled errors into readable stack traces. Costs a little CPU but
    // an error without a stack trace is close to useless during triage.
    attachStacktrace: true,

    // `enableLogs` wires Sentry's structured logger into console output, which
    // is how the performance/error dashboards get log volume. Dev stays silent
    // to avoid doubling the console noise React already produces.
    enableLogs: IS_PROD ? ["warn", "error"] : [],

    // Privacy default: do not attach IP, headers, cookies or query strings.
    // Flip to true only after a privacy review, and never alongside replay.
    sendDefaultPii: false,

    ignoreErrors: IGNORE_ERRORS,

    // Keep a thin trail of recent navigation/errors for context on the next one.
    maxBreadcrumbs: 50,

    ...overrides,
  });

  console.info(`[sentry] initialized (env=${ENVIRONMENT}, release=${RELEASE ?? "unset"})`);
}

/**
 * Report an error to Sentry with optional structured context.
 *
 * Wraps `Sentry.captureException` so call sites (route error boundaries,
 * mutation handlers) do not need to import the SDK directly, and so we have a
 * single place to add redaction or sampling rules later.
 *
 * @param {unknown} error - The thrown value. Non-Error throws (strings,
 *   rejected non-promises) are normalized so Sentry grouping stays sane.
 * @param {object} [context] - Extra data attached to the event.
 * @param {string} [context.level] - Sentry severity, e.g. "warning".
 * @param {string} [context.transaction] - Transaction the error belongs to.
 * @param {Record<string, unknown>} [context.extra] - Arbitrary detail.
 * @param {Record<string, string>} [context.tags] - Indexed, filterable labels.
 * @returns {string | undefined} The event id, used as a support reference.
 */
export function captureException(error, context = {}) {
  if (!SENTRY_ENABLED) return undefined;

  const normalized =
    error instanceof Error ? error : new Error(typeof error === "string" ? error : "Unknown error");

  // Preserve the original thrown value: a custom error class carries the fields
  // (status code, upstream provider, response body) that make an event actionable.
  if (!(error instanceof Error) && error != null) normalized.cause = error;

  return Sentry.captureException(normalized, {
    level: context.level,
    transaction: context.transaction,
    tags: context.tags,
    extra: context.extra,
  });
}

/**
 * Attach the signed-in user to subsequent events.
 *
 * Required for Sentry's "users affected" count, which is the metric that turns
 * an error rate into a business impact statement.
 *
 * @param {{ id: string, email?: string, role?: string } | null} user
 */
export function setSentryUser(user) {
  if (!SENTRY_ENABLED) return;
  if (!user) {
    clearSentryUser();
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: SEND_EMAIL ? user.email : undefined,
    username: SEND_EMAIL ? (user.email ?? undefined) : undefined,
  });

  // Roles are searchable filters in the Sentry UI ("only show superadmin errors").
  if (user.role) Sentry.setTag("role", user.role);
}

/**
 * Detach the current user. Required on sign-out: without this, events from the
 * next anonymous visitor are attributed to the previous account.
 */
export function clearSentryUser() {
  if (!SENTRY_ENABLED) return;
  Sentry.setUser(null);
}

/**
 * Event id of the most recent captured error, for display in error UI so a user
 * can quote a precise reference to support. Returns undefined when Sentry is
 * disabled or nothing has been captured yet.
 */
export function lastSentryEventId() {
  if (!SENTRY_ENABLED) return undefined;
  return Sentry.lastEventId();
}

export default {
  initSentry,
  captureException,
  setSentryUser,
  clearSentryUser,
  lastSentryEventId,
  SENTRY_ENABLED,
};
